import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Col, Container, PopupModal, Row, Spinner, TopBar } from 'components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    BanIcon,
    DeleteIcon,
    EditIcon,
    OrangeMoneyIcon,
    OutlineCheckIcon,
    WarningOutlineIcon,
    WaveMoneyIcon
} from 'assets'
import {
    buildMobilePaymentReturnUrl,
    constants,
    defaultClientContactsMatchQuery,
    formatPhone,
    getConnectUser,
    getCountryInitials,
    isDefaultClientForPhone,
    isValidSenegalPhone,
    matchingDefaultContact,
    normalizePhone,
    isPaymentResultForAttempt,
    paymentStartEvent,
    paymentWaitExitEvent,
    shouldOpenPaymentRedirect,
    toComparablePhone
} from 'helpers'
import {
    bulkEnrollmentExitStepFromRow,
    quantityChangeDirection,
    rowValidationErrorName,
    setBulkEnrollmentCurrentStep
} from 'helpers/bulkEnrollmentAnalytics'
import {
    formatBulkEnrollmentAmount,
    getBulkEnrollmentRowNames,
    getBulkEnrollmentRowTotal
} from 'helpers/bulkEnrollmentHelpers'
import {
    useAppSelector,
    useBindDispatch,
    useBulkEnrollmentAnalytics,
    useAnalytics,
    useScreenSize
} from 'hooks'
import { useMobilePayment } from 'hooks/useMobilePayment'
import { usePaymentPolling } from 'hooks/usePaymentPolling'
import { PaymentFeedbackMessage } from 'components/enrollForm'
import { colors } from 'styles/colors'
import { ClientType } from 'storeTypes'
import {
    BulkEnrollmentClientPackages,
    BulkEnrollmentThumbnails
} from './BulkEnrollmentClientPackages'
import { BulkEnrollmentFarmerIcon } from './BulkEnrollmentFarmerIcon'
import './bulkEnrollment.scss'

export const BulkEnrollmentEntry = () => {
    const navigate = useNavigate()
    const { villageId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const {
        phone_number: phoneNumber,
        phone_flotte: phoneFlotte,
        country
    } = getConnectUser()
    const { t } = useTranslation()
    const {
        trackRowConfirmed,
        trackNameCorrected,
        trackRowRemoved,
        trackValidationError,
        trackPackageQuantityChanged,
        startRowTimer
    } = useBulkEnrollmentAnalytics(villageId)
    const { trackEvent, trackTimedEvent } = useAnalytics()
    const {
        loadBulkEnrollmentClientsHandler,
        ensureBulkEnrollmentVillageHandler,
        updateBulkEnrollmentRowHandler,
        addBulkEnrollmentRowHandler,
        commitBulkEnrollmentRowHandler,
        clearBulkEnrollmentRowHandler,
        resetBulkEnrollmentHandler,
        setBulkEnrollmentActiveDropdownRowHandler,
        enrollBulkEnrollmentRowHandler,
        requestMobilePaymentHandler,
        clearMobilePaymentHandler,
        navbarHandler
    } = useBindDispatch()
    const { rows, clients, selectedPackage, activeDropdownRowId } =
        useAppSelector((state) => state.bulkEnrollment)
    const {
        loading: mobilePaymentLoading,
        response: mobilePaymentResponse,
        error: mobilePaymentError,
        status: mobilePaymentStatus,
        uuid: mobilePaymentUuid
    } = useAppSelector((state) => state.mobilePayment)
    const { isMobilePaymentEnabled, mobilePaymentProviders } =
        useMobilePayment()
    const [selectingProvider, setSelectingProvider] = useState(false)
    const [paymentInProgress, setPaymentInProgress] = useState(false)
    const [showPaymentFeedback, setShowPaymentFeedback] = useState(false)
    const [selectedProvider, setSelectedProvider] = useState('')
    const [payingRowId, setPayingRowId] = useState<string | null>(null)
    const [paymentSkipped, setPaymentSkipped] = useState(false)
    const [providerPromptOpen, setProviderPromptOpen] = useState(false)
    const paymentSkippedRef = useRef(false)
    const screenSize = useScreenSize()
    const isMobile = screenSize.screenWidth < constants.MOBILE_SIZE

    const committedRows = rows.filter((row) => row.committed)
    const activeRow = rows.find((row) => !row.committed)
    const displayedCommittedRow =
        !activeRow && committedRows.length
            ? committedRows[committedRows.length - 1]
            : null
    const avatarRows = activeRow
        ? committedRows
        : committedRows.slice(0, -1)
    const hasCommitted = committedRows.length > 0

    const agentPhones = useMemo(() => {
        return new Set(
            [phoneNumber, phoneFlotte]
                .filter(Boolean)
                .map((phone) => toComparablePhone(String(phone)))
                .filter(Boolean)
        )
    }, [phoneNumber, phoneFlotte])

    const isAgentPhone = (phone: string) => {
        const comparable = toComparablePhone(phone)
        return !!comparable && agentPhones.has(comparable)
    }

    const isDuplicatePhone = (phone: string, rowId: string) => {
        const comparable = toComparablePhone(phone)
        if (comparable.length !== 9) return false

        return rows.some(
            (row) =>
                row.id !== rowId &&
                toComparablePhone(row.phone) === comparable
        )
    }

    const hasNames = (row: typeof rows[number]) =>
        !!(row.firstname || '').trim() && !!(row.lastname || '').trim()

    const lastValidationErrorRef = useRef<Record<string, string | null>>({})

    const emitRowValidationError = (
        row: typeof rows[number],
        phoneSettled: boolean
    ) => {
        const error = rowValidationErrorName({
            phone: row.phone,
            isAgentPhone: isAgentPhone(row.phone),
            isDuplicate: isDuplicatePhone(row.phone, row.id),
            phoneSettled
        })
        if (error && lastValidationErrorRef.current[row.id] !== error) {
            trackValidationError(error)
        }
        lastValidationErrorRef.current[row.id] = error
    }

    useEffect(() => {
        if (villageId) ensureBulkEnrollmentVillageHandler(villageId)
    }, [villageId])

    useEffect(() => {
        loadBulkEnrollmentClientsHandler({
            villageId,
            veVillages: getConnectUser().veVillages
        })
    }, [villageId])

    useEffect(() => {
        const paymentResult = searchParams.get('mobile-payment')
        if (!paymentResult) return

        const returnedAmount = Number(searchParams.get('mm-amount'))
        const rowTotal =
            selectedPackage && activeRow
                ? getBulkEnrollmentRowTotal(
                      selectedPackage,
                      activeRow.quantity,
                      activeRow.selectedAddOns,
                      activeRow.matchedClient
                  )
                : NaN
        const amountMatches =
            Number.isFinite(returnedAmount) && returnedAmount === rowTotal

        const paidRowIsReady =
            !!activeRow &&
            activeRow.enrolled &&
            activeRow.confirmed &&
            hasNames(activeRow) &&
            isValidSenegalPhone(activeRow.phone) &&
            amountMatches

        if (
            paymentResult === constants.MOBILE_MONEY.SUCCESS &&
            paidRowIsReady &&
            activeRow
        ) {
            commitBulkEnrollmentRowHandler(activeRow.id)
            clearMobilePaymentHandler()
            setPaymentInProgress(false)
            setSelectingProvider(false)
            setSelectedProvider('')
            setPayingRowId(null)
        }

        if (
            paymentResult === constants.MOBILE_MONEY.SUCCESS ||
            paymentResult === constants.MOBILE_MONEY.FAILURE
        ) {
            const next = new URLSearchParams(searchParams)
            next.delete('mobile-payment')
            next.delete('mm-amount')
            setSearchParams(next, { replace: true })
        }
    }, [searchParams, activeRow?.id, activeRow?.enrolled, selectedPackage])

    // A lot cannot be submitted without a package. If we land here without one
    // (deep link, or state lost), send the agent back to package selection
    // instead of letting them fill rows that will be rejected on submit.
    useEffect(() => {
        if (!selectedPackage) {
            navigate(`/village/${villageId}/bulk-enrollment/packages`, {
                replace: true
            })
        }
    }, [selectedPackage, villageId])

    useEffect(() => {
        if (isMobile) {
            navbarHandler(constants.HIDE_PART_NAVBAR)
        } else {
            navbarHandler(constants.SHOW_NAVBAR)
        }
    }, [isMobile])

    useEffect(() => {
        const stepRow = activeRow || displayedCommittedRow
        if (stepRow) {
            setBulkEnrollmentCurrentStep(bulkEnrollmentExitStepFromRow(stepRow))
        }
    }, [rows])

    useEffect(() => {
        rows.forEach((row) => {
            emitRowValidationError(row, normalizePhone(row.phone).length >= 9)
        })
    }, [rows])

    const getMatchingClients = (phoneInput: string, rowId: string) => {
        const digits = normalizePhone(phoneInput)
        if (digits.length < 3) return []

        const selectedCodes = new Set(
            rows
                .filter((row) => row.id !== rowId)
                .map((row) => row.matchedClient?.client_code)
                .filter(Boolean)
        )

        return clients
            .filter((client: ClientType) =>
                defaultClientContactsMatchQuery(client, digits)
            )
            .filter(
                (client: ClientType) => !selectedCodes.has(client.client_code)
            )
            .slice(0, 6)
    }

    const findExactClientMatch = (phoneInput: string, rowId: string) => {
        const comparable = toComparablePhone(phoneInput)
        if (comparable.length !== 9) return null

        const selectedCodes = new Set(
            rows
                .filter((row) => row.id !== rowId)
                .map((row) => row.matchedClient?.client_code)
                .filter(Boolean)
        )

        return (
            clients.find((client: ClientType) => {
                if (selectedCodes.has(client.client_code)) return false
                return isDefaultClientForPhone(client, comparable)
            }) || null
        )
    }

    const clientPhonesMatch = (client: ClientType, phoneInput: string) =>
        isDefaultClientForPhone(client, phoneInput)

    const namesFromMatch = (client: ClientType | null) =>
        client
            ? getBulkEnrollmentRowNames(client)
            : { firstname: '', lastname: '' }

    const canShowNames = (row: typeof rows[number]) => {
        if (!isValidSenegalPhone(row.phone)) return false
        if (isAgentPhone(row.phone)) return false
        if (isDuplicatePhone(row.phone, row.id)) return false
        return true
    }

    // Payment is optional. Commit is the gate for the lot; Payer is an extra
    // step when mobile money is available, including after a failed attempt
    // that already enrolled the row.
    const canCommitRow = (row: typeof rows[number]) => {
        if (!canShowNames(row)) return false
        if (!hasNames(row) || !row.confirmed) return false
        return Number(row.quantity) >= 1
    }

    const canPayRow = (row: typeof rows[number]) =>
        canCommitRow(row) && isMobilePaymentEnabled

    const endPollingPaymentCallback = () => {
        if (paymentSkippedRef.current) return
        setShowPaymentFeedback(true)
    }

    const { startPollingPayment, stopPollingPayment } = usePaymentPolling(
        endPollingPaymentCallback
    )

    const paymentAmount = selectedPackage && activeRow
        ? getBulkEnrollmentRowTotal(
              selectedPackage,
              activeRow.quantity,
              activeRow.selectedAddOns,
              activeRow.matchedClient
          )
        : 0

    const resetPaymentUi = () => {
        setPaymentInProgress(false)
        setSelectingProvider(false)
        setSelectedProvider('')
        setPayingRowId(null)
        setShowPaymentFeedback(false)
        setProviderPromptOpen(false)
        stopPollingPayment()
        clearMobilePaymentHandler()
    }

    const startMobilePayment = async (
        row: typeof rows[number],
        provider: string
    ) => {
        if (!villageId || !selectedPackage) return

        clearMobilePaymentHandler()
        paymentSkippedRef.current = false
        setProviderPromptOpen(false)
        setPaymentSkipped(false)
        setPayingRowId(row.id)
        setPaymentInProgress(true)
        setShowPaymentFeedback(false)

        let clientCode = row.clientCode || row.matchedClient?.client_code || ''
        if (!row.enrolled) {
            // The bound thunk resolves to the dispatched action at runtime; the
            // binding helper types it as the plain action, hence the cast.
            const result = await (enrollBulkEnrollmentRowHandler({
                villageId,
                rowId: row.id
            }) as unknown as Promise<{
                meta?: { requestStatus?: string }
                payload?: { clientCode?: string }
            }>)
            if (result?.meta?.requestStatus !== 'fulfilled') {
                setPaymentInProgress(false)
                setPayingRowId(null)
                setShowPaymentFeedback(true)
                return
            }
            clientCode = result.payload?.clientCode || clientCode
        }

        if (!clientCode) {
            setPaymentInProgress(false)
            setPayingRowId(null)
            setShowPaymentFeedback(true)
            return
        }

        const amount = getBulkEnrollmentRowTotal(
            selectedPackage,
            row.quantity,
            row.selectedAddOns,
            row.matchedClient
        )

        // Each request is a Matomo payment attempt: the start pairs with the
        // payment_exit-waiting in skipPaymentHandler so exits have a denominator.
        trackEvent(paymentStartEvent())
        requestMobilePaymentHandler({
            country_code: getCountryInitials(country || ''),
            client_code: clientCode,
            provider,
            amount,
            phone_number: row.phone,
            success_url: buildMobilePaymentReturnUrl(
                window.location.href,
                'success',
                amount
            ),
            error_url: buildMobilePaymentReturnUrl(
                window.location.href,
                'failure',
                amount
            )
        })
    }

    const resolvePaymentProvider = (provider: string) =>
        provider === constants.MOBILE_MONEY.ORANGE_MONEY
            ? constants.MOBILE_MONEY.ORANGE_USSD
            : provider

    const handlePayer = (row: typeof rows[number]) => {
        const providers = mobilePaymentProviders || []
        if (providers.length === 1) {
            const provider = resolvePaymentProvider(providers[0])
            setSelectedProvider(provider)
            startMobilePayment(row, provider)
            return
        }
        setSelectingProvider(true)
    }

    const handleCommitRow = (row: typeof rows[number]) => {
        if (!canCommitRow(row)) return
        resetPaymentUi()
        commitBulkEnrollmentRowHandler(row.id)
    }

    const handleClearRow = (rowId: string) => {
        resetPaymentUi()
        trackRowRemoved()
        clearBulkEnrollmentRowHandler(rowId)
    }

    const skipPaymentHandler = () => {
        if (mobilePaymentResponse?.redirect_url) {
            setProviderPromptOpen(true)
            setShowPaymentFeedback(true)
            return
        }
        paymentSkippedRef.current = true
        setPaymentSkipped(true)
        stopPollingPayment()
        trackTimedEvent(paymentWaitExitEvent())
        resetPaymentUi()
    }

    useEffect(() => {
        if (
            !shouldOpenPaymentRedirect(
                mobilePaymentResponse,
                mobilePaymentUuid,
                paymentSkippedRef.current
            ) ||
            !paymentInProgress
        ) {
            return
        }
        if (mobilePaymentUuid) {
            startPollingPayment(mobilePaymentUuid)
        }
        window.open(
            mobilePaymentResponse?.redirect_url,
            '_self',
            'noopener,noreferrer'
        )
    }, [mobilePaymentResponse, mobilePaymentUuid, paymentInProgress])

    useEffect(() => {
        if (paymentSkippedRef.current) return
        if (!paymentInProgress || !activeRow) return
        if (
            mobilePaymentStatus === constants.MOBILE_MONEY.SUCCESS &&
            payingRowId === activeRow.id &&
            mobilePaymentUuid
        ) {
            commitBulkEnrollmentRowHandler(activeRow.id)
            resetPaymentUi()
        }
        if (mobilePaymentStatus === constants.MOBILE_MONEY.FAILURE) {
            setShowPaymentFeedback(true)
        }
    }, [mobilePaymentStatus, paymentInProgress, activeRow?.id, payingRowId, mobilePaymentUuid])

    useEffect(() => {
        if (paymentSkippedRef.current) return
        if (mobilePaymentError && paymentInProgress) {
            setShowPaymentFeedback(true)
        }
    }, [mobilePaymentError, paymentInProgress])

    const handlePhoneChange = (row: typeof rows[number], value: string) => {
        if (row.enrolled) return
        const digits = value.replace(/\D/g, '').slice(0, 9)

        setBulkEnrollmentActiveDropdownRowHandler(row.id)

        const exactMatch = findExactClientMatch(digits, row.id)
        const keptMatch =
            row.matchedClient && clientPhonesMatch(row.matchedClient, digits)
                ? row.matchedClient
                : null
        const matched = exactMatch || keptMatch
        const isSameClient =
            !!matched &&
            row.matchedClient?.client_code === matched.client_code
        const nextNames = isSameClient
            ? {
                  firstname: row.firstname,
                  lastname: row.lastname
              }
            : namesFromMatch(matched)

        let confirmed = false
        if (matched) {
            confirmed = isSameClient ? row.confirmed : true
        }

        if (confirmed && !row.confirmed) {
            trackRowConfirmed(row.id, 'existing_client')
        }

        updateBulkEnrollmentRowHandler({
            id: row.id,
            changes: {
                phone: digits,
                matchedClient: matched,
                confirmed,
                firstname: nextNames.firstname,
                lastname: nextNames.lastname
            }
        })
    }

    const renderPhoneField = (
        row: typeof rows[number],
        options: { readOnly?: boolean; showTrash?: boolean } = {}
    ) => {
        const invalidPhone = !!row.phone && !isValidSenegalPhone(row.phone)
        const agentPhone = isAgentPhone(row.phone)
        const duplicatePhone = isDuplicatePhone(row.phone, row.id)
        const matchingClients = getMatchingClients(row.phone, row.id)
        const showDropdown =
            !options.readOnly &&
            activeDropdownRowId === row.id &&
            matchingClients.length > 0 &&
            !agentPhone &&
            !duplicatePhone

        const renderRowAction = () => {
            if (options.showTrash) {
                return (
                    <button
                        type="button"
                        className="bulk-enrollment-delete-row"
                        onClick={() => handleClearRow(row.id)}
                        aria-label={t('bulkEnrollment.clearRow')}
                        id={`bulk-enrollment-delete-row-${row.id}`}
                    >
                        <DeleteIcon color="#FFFFFF" />
                    </button>
                )
            }

            if (row.committed) {
                return (
                    <span
                        className="bulk-enrollment-committed-check"
                        aria-label={t('bulkEnrollment.clientConfirmed')}
                    >
                        <OutlineCheckIcon
                            color="#fff"
                            width="16"
                            height="16"
                            strokeWidth="3"
                        />
                    </span>
                )
            }

            return null
        }

        return (
            <div className="bulk-enrollment-identity-row">
                <BulkEnrollmentFarmerIcon committed={row.committed} />
                <div className="bulk-enrollment-phone-field">
                    <div
                        className={`bulk-enrollment-phone-input ${
                            options.readOnly
                                ? 'bulk-enrollment-phone-input--readonly'
                                : ''
                        } ${
                            invalidPhone || agentPhone || duplicatePhone
                                ? 'bulk-enrollment-phone-input--invalid'
                                : ''
                        }`}
                    >
                        <input
                            value={formatPhone(row.phone)}
                            className="bulk-enrollment-phone-input__field"
                            placeholder="77 000 00 00"
                            disabled={options.readOnly}
                            onFocus={() =>
                                setBulkEnrollmentActiveDropdownRowHandler(
                                    row.id
                                )
                            }
                            onBlur={() => {
                                emitRowValidationError(row, true)
                                window.setTimeout(() => {
                                    setBulkEnrollmentActiveDropdownRowHandler(
                                        null
                                    )
                                }, 150)
                            }}
                            onChange={(e) =>
                                handlePhoneChange(row, e.target.value)
                            }
                        />
                    </div>
                    {showDropdown && (
                        <div className="bulk-enrollment-phone-dropdown">
                            {matchingClients.map((client) => (
                                <button
                                    key={client.client_code}
                                    type="button"
                                    className="bulk-enrollment-phone-dropdown__item"
                                    onMouseDown={(event) => {
                                        event.preventDefault()
                                        updateBulkEnrollmentRowHandler({
                                            id: row.id,
                                            changes: {
                                                phone: matchingDefaultContact(
                                                    client,
                                                    row.phone
                                                ),
                                                matchedClient: client,
                                                confirmed: true,
                                                ...getBulkEnrollmentRowNames(
                                                    client
                                                )
                                            }
                                        })
                                        if (!row.confirmed) {
                                            trackRowConfirmed(
                                                row.id,
                                                'existing_client'
                                            )
                                        }
                                        setBulkEnrollmentActiveDropdownRowHandler(
                                            null
                                        )
                                    }}
                                >
                                    <span className="bulk-enrollment-phone-dropdown__name">
                                        {client.fullname}
                                    </span>
                                    <span className="bulk-enrollment-phone-dropdown__meta">
                                        +221{' '}
                                        {formatPhone(
                                            matchingDefaultContact(
                                                client,
                                                row.phone
                                            )
                                        )}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {renderRowAction()}
            </div>
        )
    }

    const renderNameFields = (
        row: typeof rows[number],
        options: { readOnly?: boolean } = {}
    ) => {
        if (options.readOnly) {
            return (
                <div className="bulk-enrollment-name-row">
                    <label className="bulk-enrollment-name-field">
                        <input
                            value={row.firstname}
                            disabled
                            placeholder={t('firstName')}
                            aria-label={t('firstName')}
                            className="bulk-enrollment-name-field__input bulk-enrollment-name-field__input--locked"
                            readOnly
                        />
                    </label>
                    <label className="bulk-enrollment-name-field">
                        <input
                            value={row.lastname}
                            disabled
                            placeholder={t('lastName')}
                            aria-label={t('lastName')}
                            className="bulk-enrollment-name-field__input bulk-enrollment-name-field__input--locked"
                            readOnly
                        />
                    </label>
                </div>
            )
        }

        const invalidPhone = !!row.phone && !isValidSenegalPhone(row.phone)

        if (invalidPhone) {
            return (
                <div className="bulk-enrollment-status bulk-enrollment-status--error">
                    <WarningOutlineIcon />
                    <p>{t('bulkEnrollment.invalidPhoneNumber')}</p>
                </div>
            )
        }

        if (isAgentPhone(row.phone)) {
            return (
                <div className="bulk-enrollment-status bulk-enrollment-status--error">
                    <BanIcon />
                    <p>{t('bulkEnrollment.agentPhoneNumber')}</p>
                </div>
            )
        }

        if (isDuplicatePhone(row.phone, row.id)) {
            return (
                <div className="bulk-enrollment-status bulk-enrollment-status--error">
                    <WarningOutlineIcon />
                    <p>{t('bulkEnrollment.duplicatePhoneNumber')}</p>
                </div>
            )
        }

        if (!canShowNames(row)) return null

        const isExisting = !!row.matchedClient
        const locked = row.confirmed
        const namesReady = hasNames(row)

        return (
            <>
                {!isExisting && (
                    <div className="bulk-enrollment-new-client-pill">
                        <span className="bulk-enrollment-new-client-pill__dot" />
                        <p>{t('bulkEnrollment.newClientPill')}</p>
                    </div>
                )}
                <div className="bulk-enrollment-name-row">
                    <label className="bulk-enrollment-name-field">
                        <input
                            value={row.firstname}
                            disabled={locked}
                            placeholder={t('firstName')}
                            aria-label={t('firstName')}
                            className={`bulk-enrollment-name-field__input ${
                                locked
                                    ? 'bulk-enrollment-name-field__input--locked'
                                    : 'bulk-enrollment-name-field__input--editing'
                            }`}
                            onChange={(e) =>
                                updateBulkEnrollmentRowHandler({
                                    id: row.id,
                                    changes: { firstname: e.target.value }
                                })
                            }
                        />
                    </label>
                    <label className="bulk-enrollment-name-field">
                        <input
                            value={row.lastname}
                            disabled={locked}
                            placeholder={t('lastName')}
                            aria-label={t('lastName')}
                            className={`bulk-enrollment-name-field__input ${
                                locked
                                    ? 'bulk-enrollment-name-field__input--locked'
                                    : 'bulk-enrollment-name-field__input--editing'
                            }`}
                            onChange={(e) =>
                                updateBulkEnrollmentRowHandler({
                                    id: row.id,
                                    changes: { lastname: e.target.value }
                                })
                            }
                        />
                    </label>
                    <button
                        type="button"
                        className={`bulk-enrollment-name-toggle ${
                            locked
                                ? 'bulk-enrollment-name-toggle--locked'
                                : 'bulk-enrollment-name-toggle--editing'
                        }`}
                        aria-label={
                            locked
                                ? t('bulkEnrollment.editName')
                                : t('bulkEnrollment.confirmName')
                        }
                        disabled={!locked && !namesReady}
                        onClick={() => {
                            if (!locked && !namesReady) return
                            if (locked) {
                                trackNameCorrected(
                                    row.matchedClient
                                        ? 'existing_client'
                                        : 'new_client'
                                )
                                startRowTimer(row.id)
                            } else {
                                trackRowConfirmed(
                                    row.id,
                                    row.matchedClient
                                        ? 'existing_client'
                                        : 'new_client'
                                )
                            }
                            updateBulkEnrollmentRowHandler({
                                id: row.id,
                                changes: { confirmed: !row.confirmed }
                            })
                        }}
                    >
                        {locked ? (
                            <EditIcon color="#FFFFFF" width="18" height="18" />
                        ) : (
                            <OutlineCheckIcon
                                color="#FFFFFF"
                                width="18"
                                height="18"
                            />
                        )}
                    </button>
                </div>
            </>
        )
    }

    const closeEnrollment = () => {
        resetBulkEnrollmentHandler()
        navigate(`/village/${villageId}`)
    }

    return (
        <div className="bulk-enrollment-page page-wrapper">
            <div className="sticky-header" id="sticky-header">
                <Container>
                    <TopBar
                        back={!hasCommitted}
                        backNavigation={() =>
                            navigate(
                                `/village/${villageId}/bulk-enrollment/packages`
                            )
                        }
                        close={closeEnrollment}
                        color={colors.primary}
                    >
                        {t('bulkEnrollment.pageTitle')}
                    </TopBar>
                </Container>
            </div>
            <Container>
                <Row>
                    <Col md={12} lg={8}>
                        {selectedPackage && (
                            <div
                                className="bulk-enrollment-batch-card"
                                data-testid="bulk-enrollment-batch-card"
                            >
                                {selectedPackage.image_url ? (
                                    <img
                                        src={selectedPackage.image_url}
                                        alt={selectedPackage.name}
                                        className="bulk-enrollment-batch-card__image"
                                    />
                                ) : (
                                    <div
                                        className="bulk-enrollment-batch-card__image bulk-enrollment-batch-card__image--empty"
                                        aria-hidden="true"
                                    />
                                )}
                                <p className="bulk-enrollment-batch-card__name">
                                    {selectedPackage.name}
                                </p>
                                <p className="bulk-enrollment-batch-card__size">
                                    {selectedPackage.size_display ||
                                        selectedPackage.size}
                                </p>
                                <p className="bulk-enrollment-batch-card__price">
                                    {formatBulkEnrollmentAmount(
                                        selectedPackage.price,
                                        selectedPackage.hidePrice
                                    )}
                                </p>
                            </div>
                        )}

                        {avatarRows.length > 0 && (
                            <div
                                className="bulk-enrollment-avatars"
                                data-testid="bulk-enrollment-avatars"
                            >
                                {avatarRows.map((row) => (
                                    <span
                                        key={row.id}
                                        data-testid={`bulk-enrollment-avatar-${row.id}`}
                                    >
                                        <BulkEnrollmentFarmerIcon committed />
                                    </span>
                                ))}
                            </div>
                        )}

                        {displayedCommittedRow && selectedPackage && (
                            <div
                                className="bulk-enrollment-card bulk-enrollment-card--committed"
                                data-testid={`bulk-enrollment-committed-card-${displayedCommittedRow.id}`}
                            >
                                {renderPhoneField(displayedCommittedRow, {
                                    readOnly: true
                                })}
                                {renderNameFields(displayedCommittedRow, {
                                    readOnly: true
                                })}
                                <div className="bulk-enrollment-total-row bulk-enrollment-total-row--committed">
                                    <BulkEnrollmentThumbnails
                                        selectedPackage={selectedPackage}
                                        selectedAddOns={
                                            displayedCommittedRow.selectedAddOns ||
                                            []
                                        }
                                    />
                                    <p className="bulk-enrollment-total-row__amount bulk-enrollment-total-row__amount--committed">
                                        {formatBulkEnrollmentAmount(
                                            getBulkEnrollmentRowTotal(
                                                selectedPackage,
                                                displayedCommittedRow.quantity,
                                                displayedCommittedRow.selectedAddOns,
                                                displayedCommittedRow.matchedClient
                                            ),
                                            selectedPackage.hidePrice
                                        )}
                                    </p>
                                </div>
                            </div>
                        )}

                        {activeRow && (
                            <div
                                className="bulk-enrollment-card"
                                data-testid={`bulk-enrollment-active-card-${activeRow.id}`}
                            >
                                {renderPhoneField(activeRow, {
                                    readOnly: activeRow.enrolled,
                                    showTrash:
                                        !!activeRow.phone &&
                                        !activeRow.committed
                                })}
                                {renderNameFields(activeRow, {
                                    readOnly: activeRow.enrolled
                                })}
                                {selectedPackage && canShowNames(activeRow) && (
                                    <BulkEnrollmentClientPackages
                                        selectedPackage={selectedPackage}
                                        quantity={activeRow.quantity}
                                        selectedAddOns={
                                            activeRow.selectedAddOns || []
                                        }
                                        client={activeRow.matchedClient}
                                        readOnly={activeRow.enrolled}
                                        onQuantityChange={(quantity) => {
                                            if (activeRow.enrolled) return
                                            const direction =
                                                quantityChangeDirection(
                                                    activeRow.quantity,
                                                    quantity
                                                )
                                            if (direction) {
                                                trackPackageQuantityChanged(
                                                    Number(quantity),
                                                    direction
                                                )
                                            }
                                            updateBulkEnrollmentRowHandler({
                                                id: activeRow.id,
                                                changes: { quantity }
                                            })
                                        }}
                                        onAddOnsChange={(selectedAddOns) => {
                                            if (activeRow.enrolled) return
                                            updateBulkEnrollmentRowHandler({
                                                id: activeRow.id,
                                                changes: { selectedAddOns }
                                            })
                                        }}
                                    />
                                )}
                                {canShowNames(activeRow) && (
                                    <div className="bulk-enrollment-row-actions">
                                        {selectingProvider ? (
                                            <div className="bulk-enrollment-providers">
                                                {(mobilePaymentProviders || []).includes(
                                                    constants.MOBILE_MONEY.WAVE
                                                ) && (
                                                    <button
                                                        type="button"
                                                        className={`bulk-enrollment-providers__option ${
                                                            selectedProvider ===
                                                            constants.MOBILE_MONEY
                                                                .WAVE
                                                                ? 'bulk-enrollment-providers__option--selected'
                                                                : ''
                                                        }`}
                                                        aria-label="wave-money-icon-parent"
                                                        onClick={() =>
                                                            setSelectedProvider(
                                                                constants
                                                                    .MOBILE_MONEY
                                                                    .WAVE
                                                            )
                                                        }
                                                    >
                                                        <WaveMoneyIcon size={72} />
                                                        <span>Wave</span>
                                                    </button>
                                                )}
                                                {(mobilePaymentProviders || []).includes(
                                                    constants.MOBILE_MONEY
                                                        .ORANGE_MONEY
                                                ) && (
                                                    <button
                                                        type="button"
                                                        className={`bulk-enrollment-providers__option ${
                                                            selectedProvider ===
                                                            constants.MOBILE_MONEY
                                                                .ORANGE_USSD
                                                                ? 'bulk-enrollment-providers__option--selected'
                                                                : ''
                                                        }`}
                                                        aria-label="orange-money-icon"
                                                        onClick={() =>
                                                            setSelectedProvider(
                                                                constants
                                                                    .MOBILE_MONEY
                                                                    .ORANGE_USSD
                                                            )
                                                        }
                                                    >
                                                        <OrangeMoneyIcon />
                                                        <span>OM</span>
                                                    </button>
                                                )}
                                                {selectedProvider && (
                                                    <Button
                                                        style="primary"
                                                        className="bulk-enrollment-pay"
                                                        disable={paymentInProgress}
                                                        onClick={() =>
                                                            startMobilePayment(
                                                                activeRow,
                                                                selectedProvider
                                                            )
                                                        }
                                                        id="bulk-enrollment-pay-continue"
                                                    >
                                                        {t('continue')}
                                                    </Button>
                                                )}
                                            </div>
                                        ) : (
                                            <Button
                                                style="primary"
                                                className="bulk-enrollment-pay"
                                                disable={
                                                    !canPayRow(activeRow) ||
                                                    paymentInProgress
                                                }
                                                onClick={() =>
                                                    handlePayer(activeRow)
                                                }
                                                id="bulk-enrollment-pay"
                                            >
                                                {t('pay')}
                                            </Button>
                                        )}
                                        <Button
                                            style="outline"
                                            className="bulk-enrollment-confirm"
                                            disable={!canCommitRow(activeRow)}
                                            onClick={() =>
                                                handleCommitRow(activeRow)
                                            }
                                            id="bulk-enrollment-confirm"
                                        >
                                            {t('bulkEnrollment.confirm')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {displayedCommittedRow && (
                            <Button
                                style="outline"
                                className="bulk-enrollment-add-client"
                                onClick={() => {
                                    resetPaymentUi()
                                    const rowId = `${Date.now()}`
                                    addBulkEnrollmentRowHandler(rowId)
                                    startRowTimer(rowId)
                                }}
                                id="bulk-enrollment-add-client"
                            >
                                {t('bulkEnrollment.add')}
                            </Button>
                        )}
                    </Col>
                </Row>
            </Container>
            {hasCommitted && (
                <div className="bulk-enrollment-footer">
                    <Button
                        style="primary"
                        onClick={() =>
                            navigate(
                                `/village/${villageId}/bulk-enrollment/confirmation`
                            )
                        }
                        id="bulk-enrollment-validate"
                    >
                        {t('bulkEnrollment.validateLot')}
                    </Button>
                </div>
            )}
            {paymentInProgress &&
                !paymentSkipped &&
                !providerPromptOpen &&
                (mobilePaymentLoading ||
                    (Boolean(mobilePaymentResponse?.redirect_url) &&
                        isPaymentResultForAttempt(
                            mobilePaymentResponse?.uuid,
                            mobilePaymentUuid
                        ) &&
                        mobilePaymentStatus !==
                            constants.MOBILE_MONEY.SUCCESS &&
                        mobilePaymentStatus !==
                            constants.MOBILE_MONEY.FAILURE)) && (
                <div
                    className="mobile-money--loading-wrapper"
                    aria-label="mobile-money-loading"
                >
                    <Spinner fullscreen size="100" />
                    <div className="payment-wait__skip">
                        <Button
                            style="outline"
                            type="button"
                            onClick={skipPaymentHandler}
                            id="skip-payment"
                        >
                            {t('mobileMoney.skipPayment')}
                        </Button>
                    </div>
                </div>
            )}
            {showPaymentFeedback && (
                <PopupModal
                    closeModal={() => {
                        resetPaymentUi()
                    }}
                >
                    <PaymentFeedbackMessage
                        status={
                            mobilePaymentStatus ||
                            (providerPromptOpen
                                ? constants.MOBILE_MONEY.PENDING
                                : constants.MOBILE_MONEY.FAILURE)
                        }
                        amount={paymentAmount}
                        t={t}
                        continueHandler={() => {
                            resetPaymentUi()
                        }}
                        retryHandler={() => {
                            setShowPaymentFeedback(false)
                            if (activeRow && selectedProvider) {
                                startMobilePayment(activeRow, selectedProvider)
                            } else if (activeRow) {
                                handlePayer(activeRow)
                            }
                        }}
                    />
                </PopupModal>
            )}
        </div>
    )
}
